import { useEffect, useState } from 'react';
import { Grid, IconButton, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { useAuth } from '../../../hooks/use-auth.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { env } from '../../../env';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { getUsers } from '../../../services/user.service';
import { UserAdminDto } from '../../../services/dtos/user-admin.dto';
import StyledTable from '../../../components/commun/table';
import { ConnectWithoutContact } from '@mui/icons-material';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import NoData from '../../../components/commun/no-data';

//Délai avant de lancer la recherche après la dernière frappe, pour éviter une requête par caractère saisi
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Page de moteur de recherche
 * @constructor
 */
export default function LoginAsPage() {
    //AuthContext
    let auth = useAuth();
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //UseState
    const [page, setPage] = useState<number>(0);
    const [searching, setSearching] = useState<boolean>(false);
    const [users, setUsers] = useState<ResultSearchDto<UserAdminDto>>();
    const [itemsPerPage, setItemsPerPage] = useState<number>(Number(env.REACT_APP_NUMBER_ELEMENT_TABLE));
    //Valeur saisie dans le champ de recherche
    const [searchInput, setSearchInput] = useState<string>('');
    //Valeur de recherche réellement utilisée pour l'appel API, après un court délai (debounce)
    const [search, setSearch] = useState<string>('');

    //Débounce : on ne déclenche la recherche qu'après une pause dans la saisie
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [searchInput]);

    /**
     * Récupération des contrats associés à l'utilisateur connecté
     */
    useEffect(() => {
        setSearching(true);
        getUsers(page + 1, itemsPerPage, search)
            .then(result => {
                //Changement d'état lors d'un succès du form et des users
                setUsers(result);
            })
            .catch(error => {
                //Affichage d'error
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                //Fin recherche
                setSearching(false);
            });
    }, [itemsPerPage, page, search, showMessage]);

    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<UserAdminDto, keyof UserAdminDto>[] = [
        { id: 'id', label: 'Identifiant', sort: true, width: 20 },
        { id: 'email', label: 'Mail', sort: true, width: 70 },
    ];

    /**
     * Gestion du changement de page, avec la récupération d'une autre page de factures
     * @param event Événement
     * @param newPage Page à afficher
     */
    const handleChangePage = async (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        event?.preventDefault();
        setPage(newPage);
    };

    /**
     * Gestion du changement du nombre de lignes affichées par page
     * @param event Événement
     */
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setItemsPerPage(Number(event.target.value));
        setPage(0);
    };

    /**
     * Gestion du changement du texte de recherche (email)
     * @param event Événement
     */
    const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value);
    };

    //Permet de changer de page
    const handlerLoggedInAs = (id: string, email: string) => {
        auth.loggedInAs(id, email);
    };

    // L'action est fabriqué à la main pour la tableau car il est personnalisable
    const action = (userAdmin: UserAdminDto) => {
        return (
            // Tooltip qui permet de faire l'action :
            // Se connecter en tant que
            <Tooltip title={'Se connecter en tant que'} arrow>
                <IconButton
                    data-testid={'action-connexion'}
                    onClick={() => {
                        handlerLoggedInAs(userAdmin.id, userAdmin.email);
                    }}
                >
                    <ConnectWithoutContact />
                </IconButton>
            </Tooltip>
        );
    };

    //Gestion d'affichage de la vue
    const showTable = () => {
        if (users === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }

        if (users?.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        } else {
            return (
                <StyledTable
                    data={users.items}
                    headCells={headCells}
                    searching={searching}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    action={action}
                    page={page}
                    itemsPerPage={itemsPerPage}
                    totalItems={users.totalItems}
                />
            );
        }
    };

    //Affichage de la page
    return (
        <>
            <Typography variant="h5"></Typography>
            <Grid container spacing={2} direction="column">
                <Grid item>
                    <TextField
                        value={searchInput}
                        onChange={handleChangeSearch}
                        label="Rechercher un compte"
                        placeholder="Adresse mail..."
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <SearchOutlinedIcon />,
                        }}
                    />
                </Grid>
                {/*resultat de la recherche*/}
                <Grid item>{showTable()}</Grid>
            </Grid>
        </>
    );
}
